import {
  Breadcrumbs as MuiBreadcrumbs,
  Stack,
  Box,
  Tooltip,
  IconButton,
  StackProps
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { ArrowBack } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import ContractDialog from "../dialogs/ContractDialog";
import { DialogMode } from "../../types/dialogmode";
import {
  useGetContractQuery,
  useGetSettingsQuery
} from "../../redux/slices/indexApiSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import VendorDialog from "../dialogs/VendorDialog";
import { useGetVendorQuery } from "../../redux/slices/vendorsApiSlice";
import SubscriptionInfo from "../navbar/SubscriptionInfo";
import useAuth from "../../hooks/useAuth";

// Extend the MUI Breadcrumbs type with additional props
interface BreadcrumbsBarProps extends StackProps {
  showBackButton?: boolean;
  showActionButtons?: boolean;
  showSubscriptionInfo?: boolean;
}
const MainStack = styled(Stack)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 35px;
`;

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const ButtonZone = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Item = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BreadCrumbsBar = ({
  children,
  showBackButton = true,
  showActionButtons = true,
  showSubscriptionInfo = true,
  ...restProps
}: BreadcrumbsBarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clientId, contractId, vendorId } = useParams();
  const [mode, setMode] = useState<DialogMode>(DialogMode.ViewOnly);

  const { data: contract } = useGetContractQuery(contractId ?? skipToken);
  const { data: vendor } = useGetVendorQuery(vendorId ?? skipToken);
  const { data: settings } = useGetSettingsQuery();

  useEffect(() => {
    if (
      contract?.adminIds?.some((id) => id === user.id) ||
      contract?.managerIds?.some((id) => id === user.id) ||
      contract?.ownerIds?.some((id) => id === user.id) ||
      settings?.superAdmins?.some((id: string) => id === user.id)
    ) {
      setMode(DialogMode.Edit);
    } else if (contract?.readerIds?.some((id) => id === user.id)) {
      setMode(DialogMode.ViewOnly);
    } else {
      setMode(DialogMode.ViewOnly);
    }
  }, [
    contract?.adminIds,
    contract?.managerIds,
    contract?.ownerIds,
    contract?.readerIds,
    settings?.superAdmins,
    user.id
  ]);

  const backToList = () => {
    if (contract) {
      if (clientId) {
        navigate(`/contracts-under-management/${clientId}/contracts`);
      } else {
        navigate("/contracts");
      }
    } else if (vendor) {
      navigate("/vendors");
    }
  };

  return (
    <MainStack {...restProps}>
      {/* Left side - Back button, Breadcrumbs, and Action buttons */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Back button */}
        {showBackButton && (
          <Item>
            <Tooltip title={t("Back")}>
              <IconButton size="small" onClick={() => backToList()}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
          </Item>
        )}

        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="Breadcrumb" mt={2}>
          {children}
        </Breadcrumbs>

        {/* Action buttons */}
        {showActionButtons && (
          <ButtonZone>
            {contractId && contract && mode === DialogMode.Edit && (
              <Item key={`button-contract-edit`}>
                <ContractDialog
                  contract={contract}
                  mode={DialogMode.Edit}
                  iconOnly
                />
              </Item>
            )}

            {vendorId && vendor && (
              <Item key={`button-vendor-edit`}>
                <VendorDialog vendor={vendor} mode={DialogMode.Edit} iconOnly />
              </Item>
            )}

            {contractId && contract && mode === DialogMode.Edit && (
              <Item key={`button-contract-delete`}>
                <ContractDialog
                  contract={contract}
                  mode={DialogMode.Delete}
                  iconOnly
                />
              </Item>
            )}

            {vendorId && vendor && (
              <Item key={`button-vendor-delete`}>
                <VendorDialog
                  vendor={vendor}
                  mode={DialogMode.Delete}
                  iconOnly
                />
              </Item>
            )}
          </ButtonZone>
        )}
      </Stack>

      {/* Right side - Subscription Info */}
      {showSubscriptionInfo && (
        <Item>
          <SubscriptionInfo />
        </Item>
      )}
    </MainStack>
  );
};

export default BreadCrumbsBar;
